import { useEffect, useState } from "react";
import { Loader2, AlertTriangle, BellRing, Send, Save, CalendarClock } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const num = (v) => Number(v || 0).toLocaleString();
const RUNAWAY_USD = 0.25;  // one call costing more than this is worth a look

const Stat = ({ label, value, sub, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className="mt-0.5 text-sm font-semibold text-slate-800">{value}</div>
    {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
  </div>
);

const Table = ({ headers, children, testid }) => (
  <div className="overflow-auto rounded-lg border border-slate-200">
    <table className="w-full text-xs" data-testid={testid}>
      <thead className="bg-[#33456b] text-white">
        <tr>{headers.map((h) => <th key={h} className="px-2.5 py-1.5 text-left font-semibold">{h}</th>)}</tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const SpendAlerts = () => {
  const [cfg, setCfg] = useState(null);
  const [recipients, setRecipients] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/ai-spend/alerts")
      .then((r) => { setCfg(r.data); setRecipients((r.data.recipients || []).join(", ")); })
      .catch(() => {});
  }, []);

  if (!cfg) return null;
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const payload = () => ({
    enabled: !!cfg.enabled,
    weekly_digest: !!cfg.weekly_digest,
    per_call_usd: Number(cfg.per_call_usd) || 0,
    daily_usd: Number(cfg.daily_usd) || 0,
    monthly_usd: Number(cfg.monthly_usd) || 0,
    recipients: recipients.split(",").map((s) => s.trim()).filter(Boolean),
  });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/ai-spend/alerts", payload());
      setCfg(data);
      setRecipients((data.recipients || []).join(", "));
      toast.success(data.enabled ? "Spend alerts are on" : "Spend alerts saved (off)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save alerts");
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai-spend/alerts/test");
      if (data.status === "paused") toast.warning(data.note, { duration: 9000 });
      else toast.success(`Test email sent to ${data.sent_to.join(", ")}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test email failed");
    } finally { setBusy(false); }
  };

  const sendDigest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai-spend/digest/send-now");
      if (data.status === "paused") toast.warning(data.note, { duration: 9000 });
      else toast.success(`Digest sent to ${data.sent_to.join(", ")} ($${Number(data.total_cost).toFixed(2)} last week)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Digest could not be sent");
    } finally { setBusy(false); }
  };

  const LIMITS = [
    ["per_call_usd", "Per call", "One call costing more than this emails you immediately"],
    ["daily_usd", "Per day", "Re-alerts at every multiple (2x, 3x…) of the daily total"],
    ["monthly_usd", "Per month", "Your monthly ceiling"],
  ];

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="ai-spend-alerts">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BellRing className="h-4 w-4 text-primary" />
          <h4 className="text-sm font-semibold text-slate-800">Spend alerts</h4>
          {!cfg.email_enabled && (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700">
              email not configured
            </span>
          )}
          {cfg.emails_paused && (
            <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-rose-700"
              title="Outbound email is paused platform-wide — unpause in Settings → Platform so alerts can leave"
              data-testid="ai-spend-alerts-paused">
              outbound email paused
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">{cfg.enabled ? "On" : "Off"}</span>
          <Switch checked={!!cfg.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="ai-spend-alerts-toggle" />
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {LIMITS.map(([k, label, hint]) => (
          <div key={k}>
            <label className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label} (USD)</label>
            <Input type="number" min="0" step="0.25" value={cfg[k] ?? 0} className="mt-1 h-8 text-xs"
              onChange={(e) => set(k, e.target.value)} data-testid={`ai-spend-limit-${k}`} />
            <p className="mt-1 text-[10px] leading-snug text-slate-400">{hint}</p>
          </div>
        ))}
      </div>

      <div className="mt-3">
        <label className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Email to (comma separated)</label>
        <Input value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="keith@unitedcloud.ca, julian@unitedcloud.ca"
          className="mt-1 h-8 text-xs" data-testid="ai-spend-recipients" />
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3">
        <div>
          <div className="text-xs font-medium text-slate-700">Weekly digest</div>
          <p className="text-[10px] text-slate-400">Monday 8:00 am MDT — last week's spend by feature, to the same recipients</p>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={!!cfg.weekly_digest} onCheckedChange={(v) => set("weekly_digest", v)} data-testid="ai-spend-digest-toggle" />
          <Button size="sm" variant="outline" onClick={sendDigest} disabled={busy} data-testid="ai-spend-digest-send">
            <CalendarClock className="h-3.5 w-3.5" /> Send now
          </Button>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Button size="sm" onClick={save} disabled={busy} data-testid="ai-spend-alerts-save">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
        </Button>
        <Button size="sm" variant="outline" onClick={sendTest} disabled={busy} data-testid="ai-spend-alerts-test">
          <Send className="h-3.5 w-3.5" /> Send test email
        </Button>
      </div>
    </div>
  );
};

export const AiSpendPanel = () => {
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/ai-spend${month ? `?month=${month}` : ""}`)
      .then((r) => { setData(r.data); setError(""); if (!month) setMonth(r.data.month); })
      .catch(() => setError("Could not load AI spend."))
      .finally(() => setLoading(false));
  }, [month]);

  if (loading && !data) {
    return <div className="flex items-center gap-2 py-6 text-sm text-slate-400" data-testid="ai-spend-loading">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading Claude spend…
    </div>;
  }
  if (error) return <div className="py-6 text-sm text-rose-600" data-testid="ai-spend-error">{error}</div>;
  if (!data) return null;

  const t = data.totals || {};
  const hasData = (data.by_feature || []).length > 0;

  return (
    <div className="space-y-4" data-testid="ai-spend-panel">
      <SpendAlerts />
      <div className="flex flex-wrap items-center gap-3">
        <Select value={data.month} onValueChange={setMonth}>
          <SelectTrigger className="h-8 w-40 text-xs" data-testid="ai-spend-month"><SelectValue /></SelectTrigger>
          <SelectContent>
            {(data.months || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-[11px] text-slate-400">
          Estimated from Anthropic list pricing · direct API key
        </span>
      </div>

      {!hasData ? (
        <p className="py-6 text-center text-xs text-slate-400" data-testid="ai-spend-empty">
          No Claude calls recorded for {data.month} yet.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Est. cost" value={money(t.cost_usd)} testid="ai-spend-cost" />
            <Stat label="Calls" value={num(t.calls)} testid="ai-spend-calls" />
            <Stat label="Input tokens" value={num(t.input_tokens)}
              sub={t.cache_read_tokens ? `${num(t.cache_read_tokens)} from cache` : null} testid="ai-spend-input" />
            <Stat label="Output tokens" value={num(t.output_tokens)} testid="ai-spend-output" />
          </div>

          <div className="space-y-1.5">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">By feature</h4>
            <Table headers={["Feature", "Calls", "Input", "Output", "Est. cost"]} testid="ai-spend-feature-table">
              {data.by_feature.map((r, i) => (
                <tr key={r.feature} className={i % 2 ? "bg-slate-50" : "bg-white"} data-testid={`ai-spend-feature-${r.feature}`}>
                  <td className="px-2.5 py-1.5 font-medium text-slate-700">{r.label}</td>
                  <td className="px-2.5 py-1.5 text-slate-600">{num(r.calls)}</td>
                  <td className="px-2.5 py-1.5 text-slate-600">{num(r.input_tokens)}</td>
                  <td className="px-2.5 py-1.5 text-slate-600">{num(r.output_tokens)}</td>
                  <td className="px-2.5 py-1.5 font-semibold text-slate-800">{money(r.cost_usd)}</td>
                </tr>
              ))}
            </Table>
          </div>

          <div className="space-y-1.5">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">By user</h4>
            <Table headers={["User", "Calls", "Tokens", "Est. cost"]} testid="ai-spend-user-table">
              {data.by_user.map((r, i) => (
                <tr key={`${r.user_id}-${i}`} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                  <td className="px-2.5 py-1.5 font-medium text-slate-700">{r.name}</td>
                  <td className="px-2.5 py-1.5 text-slate-600">{num(r.calls)}</td>
                  <td className="px-2.5 py-1.5 text-slate-600">{num(r.total_tokens)}</td>
                  <td className="px-2.5 py-1.5 font-semibold text-slate-800">{money(r.cost_usd)}</td>
                </tr>
              ))}
            </Table>
          </div>

          <div className="space-y-1.5">
            <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Top 10 most expensive calls
              <span className="font-normal normal-case tracking-normal text-slate-400">— a runaway trace shows up here first</span>
            </h4>
            <Table headers={["When", "Feature", "Label", "User", "Input", "Output", "Est. cost"]} testid="ai-spend-top-table">
              {data.top_calls.map((c, i) => {
                const hot = Number(c.cost_usd || 0) >= RUNAWAY_USD;
                return (
                  <tr key={i} className={i % 2 ? "bg-slate-50" : "bg-white"} data-testid={`ai-spend-top-${i}`}>
                    <td className="px-2.5 py-1.5 text-slate-500">{(c.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-2.5 py-1.5 text-slate-600">{c.feature}</td>
                    <td className="px-2.5 py-1.5 font-mono text-[11px] text-slate-500">{c.label}</td>
                    <td className="px-2.5 py-1.5 text-slate-600">{c.user_name || "System"}</td>
                    <td className="px-2.5 py-1.5 text-slate-600">{num(c.input_tokens)}</td>
                    <td className="px-2.5 py-1.5 text-slate-600">{num(c.output_tokens)}</td>
                    <td className={`px-2.5 py-1.5 font-semibold ${hot ? "text-rose-600" : "text-slate-800"}`}>
                      <span className="inline-flex items-center gap-1">
                        {hot && <AlertTriangle className="h-3 w-3" />}{money(c.cost_usd)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </Table>
          </div>
        </>
      )}
    </div>
  );
};

export default AiSpendPanel;
